import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { contentItemsAPI, contentPOsAPI, taxonomyAPI, workbackTemplateAPI, preferencesAPI, customFieldsAPI, contentProjectsAPI, purchaseOrdersAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { EntityCustomFields, renderCustomValue } from '../components/shared/EntityCustomFields';
import { CustomFieldFilters, matchesCustomFilters } from '../components/shared/CustomFieldFilters';
import { ColumnFilterPopover } from '../components/shared/ColumnFilterPopover';
import { POKindTag } from '../components/shared/POKindTag';
import { ActiveFiltersBar } from '../components/shared/ActiveFiltersBar';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../components/ui/dialog';
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription,
  AlertDialogFooter, AlertDialogAction, AlertDialogCancel,
} from '../components/ui/alert-dialog';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import { TablePagination, PAGE_SIZE } from '../components/shared/TablePagination';
import {
  Plus, Search, Layers, BookOpen, RefreshCw, Sparkles, DollarSign,
  Upload, Download, FileSpreadsheet, AlertCircle, CheckCircle2, CalendarClock, Trash2,
  Link2, LinkIcon, Info, Columns3, Wand2, ArrowUp, ArrowDown, ArrowUpDown, PencilLine, X,
  FolderKanban, User, Receipt, ListChecks,
} from 'lucide-react';
import { toast } from 'sonner';
import { CatalogLinkDialog } from '../components/content-items/CatalogLinkDialog';
import { ContentProjectFormDialog } from '../components/content-items/ContentProjectFormDialog';
import { ProjectStatusBar } from '../components/content-items/ProjectStatusBar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Checkbox } from '../components/ui/checkbox';
import { Textarea } from '../components/ui/textarea';

export const TECHNOLOGY_OPTIONS = ['Microsoft Fabric', 'Azure', 'Copilot', 'Security', 'Power Platform', 'Dynamics 365', 'Modern Work', 'Other'];
export const TRACK_OPTIONS = [
  { value: 'sales', label: 'Sales-ready' },
  { value: 'presales', label: 'Tech Deal Ready' },
  { value: 'technical', label: 'Project-Ready' },
];
export const BUILD_TYPE_OPTIONS = [
  { value: 'new_standalone', label: 'New Standalone Course' },
  { value: 'refresh', label: 'Refresh (existing course)' },
  { value: 'new_sku', label: 'New SKU (of existing course)' },
];
export const MS_PRIORITY_OPTIONS = [
  { value: 'P1', label: 'P1 — Highest Priority' },
  { value: 'P2', label: 'P2 — Medium Priority' },
  { value: 'P3', label: 'P3 — Lower Priority' },
];

// Ranks Workback Templates by how well their Asset Type / Readiness Stage tags match the item's,
// so the best fit surfaces first — the PM can still freely pick any template.
export const rankWorkbackTemplates = (templates, assetType, readinessStage) => {
  const score = (t) => {
    let s = 0;
    if (t.asset_type) s += t.asset_type === assetType ? 2 : -3;
    if (t.readiness_stage) s += t.readiness_stage === readinessStage ? 2 : -3;
    return s;
  };
  return templates
    .map(t => ({ ...t, _score: score(t), _suggested: score(t) >= 2 }))
    .sort((a, b) => b._score - a._score);
};
export const MS_PRIORITY_STYLES = {
  P1: 'bg-red-100 text-red-700',
  P2: 'bg-amber-100 text-amber-700',
  P3: 'bg-slate-100 text-slate-700',
};
export const CATALOG_STATUS_STYLES = {
  draft: { bg: 'bg-gray-100 text-gray-700', label: 'Draft' },
  proposed: { bg: 'bg-blue-100 text-blue-800', label: 'Proposed' },
  in_build: { bg: 'bg-amber-100 text-amber-800', label: 'In Build' },
  available: { bg: 'bg-green-100 text-green-800', label: 'Available' },
  retired: { bg: 'bg-red-100 text-red-700', label: 'Retired' },
};
const BUILD_TYPE_ICON = { new_standalone: BookOpen, refresh: RefreshCw, new_sku: Sparkles };

const emptyForm = {
  title: '', technology_area: '', technology_area_other: '', solution_area: '', asset_type: '', content_track: '',
  build_type: 'new_standalone', parent_item_id: '', ms_priority: '', content_po_id: '', po_amount: '',
  po_receipt_date: '', workback_template_id: '', target_go_live_date: '', go_live_override_reason: '', custom_fields: {},
  project_id: '',
};

const ALL_COLUMNS = [
  { key: 'featured_workload', label: 'Featured Workload' },
  { key: 'solution_area', label: 'Solution Area' },
  { key: 'asset_type', label: 'Asset Type' },
  { key: 'readiness_stage', label: 'Readiness Stage' },
  { key: 'build_type', label: 'Build Type' },
  { key: 'ms_priority', label: 'MS Priority' },
  { key: 'project', label: 'Project' },
  { key: 'current_phase', label: 'Current Phase' },
  { key: 'catalog_status', label: 'Catalog Status' },
  { key: 'catalog_link', label: 'Catalog Link' },
  { key: 'ms_course_id', label: 'MS Course ID' },
  { key: 'target_go_live', label: 'Target Go-Live' },
  { key: 'po_amount', label: 'PO Amount' },
  { key: 'linked_deliveries', label: 'Linked Deliveries' },
];

export const ContentItemsPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isSuperAdmin, user } = useAuth();
  const superAdmin = !!(isSuperAdmin && isSuperAdmin());
  const [tab, setTab] = useState(searchParams.get('tab') === 'projects' ? 'projects' : 'tasks');
  const [availabilityFilter, setAvailabilityFilter] = useState(searchParams.get('availability') || '');
  const [flagFilter, setFlagFilter] = useState(searchParams.get('flag') || '');
  const [items, setItems] = useState([]);
  const [projects, setProjects] = useState([]);
  const [projectDialogOpen, setProjectDialogOpen] = useState(false);
  const [editProject, setEditProject] = useState(null);
  const [projectFromSelection, setProjectFromSelection] = useState(false);
  const [projSearch, setProjSearch] = useState('');
  const [projPage, setProjPage] = useState(1);
  const [projColFilters, setProjColFilters] = useState({ ms_owner: new Set(), pm_owner: new Set(), linked_po_number: new Set() });
  const [projOverdueOnly, setProjOverdueOnly] = useState(false);
  const setProjColFilter = (key, s) => setProjColFilters(prev => ({ ...prev, [key]: s }));
  const isProjectOverdue = (p) => {
    if (!p?.target_delivery_date) return false;
    const today = new Date().toISOString().slice(0, 10);
    return p.target_delivery_date < today && (p.status_rollup?.in_build || 0) > 0;
  };
  const [standalones, setStandalones] = useState([]);
  const [pos, setPos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [trackFilter, setTrackFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [catalogDialogOpen, setCatalogDialogOpen] = useState(false);
  const [selectedCatalogCourse, setSelectedCatalogCourse] = useState(null);
  const [catalogFilter, setCatalogFilter] = useState('all');
  const [deliveryFilter, setDeliveryFilter] = useState('all');
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [importOpen, setImportOpen] = useState(false);
  const [importLoading, setImportLoading] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [taxonomyMap, setTaxonomyMap] = useState({});
  const [categories, setCategories] = useState([]);
  const [customFieldKeys, setCustomFieldKeys] = useState([]);
  const [contentFields, setContentFields] = useState([]);
  const [customFilters, setCustomFilters] = useState({});
  const [visibleColumns, setVisibleColumns] = useState(ALL_COLUMNS.map(c => c.key));
  const [workbackTemplates, setWorkbackTemplates] = useState([]);
  const [calculatedGoLive, setCalculatedGoLive] = useState('');
  const [sortBy, setSortBy] = useState(null);
  const [sortDir, setSortDir] = useState('asc');
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkEditOpen, setBulkEditOpen] = useState(false);
  const [bulkField, setBulkField] = useState('');
  const [bulkValue, setBulkValue] = useState('');
  const [bulkApplying, setBulkApplying] = useState(false);
  const [contentPage, setContentPage] = useState(1);
  const [colFilters, setColFilters] = useState({ technology_area: new Set(), solution_area: new Set(), asset_type: new Set(), content_track: new Set(), build_type: new Set(), ms_priority: new Set(), catalog_status: new Set(), project: new Set() });
  const [customColFilters, setCustomColFilters] = useState({});
  const setColFilter = (key, s) => setColFilters(prev => ({ ...prev, [key]: s }));
  const setCustomColFilter = (tid, s) => setCustomColFilters(prev => ({ ...prev, [tid]: s }));
  const [duplicateTitleError, setDuplicateTitleError] = useState('');
  const [similarSuggestions, setSimilarSuggestions] = useState([]);
  const [suggestionDismissed, setSuggestionDismissed] = useState(false);
  const importFileRef = useRef();

  const loadContentFields = useCallback(() => {
    customFieldsAPI.list('content_item').then(setContentFields).catch(() => {});
  }, []);

  useEffect(() => {
    taxonomyAPI.getAll().then(cats => {
      const map = {};
      cats.forEach(c => { map[c.id] = c.items.filter(i => i.active); });
      setTaxonomyMap(map);
      setCategories(cats);
    }).catch(() => {});
    loadContentFields();
    workbackTemplateAPI.list().then(setWorkbackTemplates).catch(() => {});
    preferencesAPI.get().then(prefs => {
      if (Array.isArray(prefs.content_items_columns) && prefs.content_items_columns.length) {
        setVisibleColumns(prefs.content_items_columns);
      }
    }).catch(() => {});
  }, []);

  const toggleColumn = (key) => {
    setVisibleColumns(prev => {
      const next = prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key];
      preferencesAPI.set('content_items_columns', next).catch(() => {});
      return next;
    });
  };
  const isColVisible = (key) => visibleColumns.includes(key);

  const toggleSort = (key) => {
    if (sortBy === key) setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortBy(key); setSortDir('asc'); }
  };
  const SortHeader = ({ sortKey, children }) => (
    <button
      type="button"
      onClick={() => toggleSort(sortKey)}
      className="flex items-center gap-1 hover:text-foreground"
      data-testid={`sort-header-${sortKey}`}
    >
      {children}
      {sortBy === sortKey ? (sortDir === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />) : <ArrowUpDown className="w-3 h-3 opacity-40" />}
    </button>
  );

  const toggleSelectAll = () => {
    setSelectedIds(prev => (prev.size === filtered.length ? new Set() : new Set(filtered.map(i => i.id))));
  };
  const toggleSelectRow = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const customCategories = categories.filter(c => !c.is_core);
  // Attached content-item custom fields (schema-level) used for form + table columns
  const contentCustomColumns = contentFields.map(f => ({ key: `custom:${f.taxonomy_id}`, label: f.label, taxonomy_id: f.taxonomy_id, options: f.options || [] }));
  const BULK_EDIT_FIELDS = [
    { key: 'solution_area', label: 'Solution Area' },
    { key: 'asset_type', label: 'Asset Type' },
    { key: 'ms_priority', label: 'MS Priority' },
    { key: 'build_type', label: 'Build Type' },
    { key: 'content_track', label: 'Readiness Stage' },
    { key: 'current_phase', label: 'Current Phase' },
    { key: 'project', label: 'Project' },
    ...contentCustomColumns.map(c => ({ key: c.key, label: c.label })),
  ];
  const bulkValueOptions = () => {
    if (bulkField === 'solution_area') return solutionAreaOptions;
    if (bulkField === 'asset_type') return assetTypeOptions;
    if (bulkField === 'ms_priority') return priorityOptions.map(p => ({ value: p.value, label: p.label || p.value }));
    if (bulkField === 'build_type') return buildTypeOptions;
    if (bulkField === 'content_track') return readinessOptions;
    if (bulkField === 'project') return projects.map(p => ({ value: p.id, label: p.name }));
    if (bulkField?.startsWith('custom:')) return taxonomyMap[bulkField.split(':')[1]] || [];
    if (bulkField === 'current_phase') {
      const map = new Map();
      items.filter(i => selectedIds.has(i.id)).forEach(i => (i.phases || []).forEach(p => {
        if (!map.has(p.phase_number)) map.set(p.phase_number, p.phase_name);
      }));
      return Array.from(map.entries()).sort((a, b) => a[0] - b[0]).map(([num, name]) => ({ value: String(num), label: `${num}. ${name}` }));
    }
    return [];
  };
  const handleBulkEditApply = async () => {
    if (!bulkField || !bulkValue) return;
    setBulkApplying(true);
    try {
      const res = await contentItemsAPI.bulkUpdate(Array.from(selectedIds), bulkField, bulkValue);
      toast.success(`Updated ${res.updated} item(s)`);
      setBulkEditOpen(false);
      setSelectedIds(new Set());
      setBulkField('');
      setBulkValue('');
      loadItems();
      loadProjects();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Bulk update failed');
    } finally { setBulkApplying(false); }
  };

  const featuredWorkloadOptions = taxonomyMap.featured_workload?.length ? taxonomyMap.featured_workload : TECHNOLOGY_OPTIONS.map(t => ({ value: t, label: t }));
  const readinessOptions = taxonomyMap.readiness_stage?.length ? taxonomyMap.readiness_stage : TRACK_OPTIONS;
  const solutionAreaOptions = taxonomyMap.solution_area || [];
  const assetTypeOptions = taxonomyMap.asset_type || [];
  const buildTypeOptions = taxonomyMap.build_type?.length ? taxonomyMap.build_type : BUILD_TYPE_OPTIONS;
  const priorityOptions = taxonomyMap.ms_priority?.length ? taxonomyMap.ms_priority : MS_PRIORITY_OPTIONS;
  const msOwnerOptions = taxonomyMap.ms_owner || [];
  const pmOwnerOptions = taxonomyMap.pmo || taxonomyMap.pm_owner || [];
  const readinessLabel = (value) => readinessOptions.find(t => t.value === value)?.label || value;
  const addableCustomCategories = customCategories.filter(c => !customFieldKeys.includes(c.id));

  const loadItems = useCallback(async () => {
    try {
      setLoading(true);
      const data = await contentItemsAPI.getAll({});
      setItems(data);
    } catch { toast.error('Failed to load content items'); }
    finally { setLoading(false); }
  }, []);

  const loadProjects = useCallback(() => {
    contentProjectsAPI.getAll().then(setProjects).catch(() => {});
  }, []);

  useEffect(() => { loadItems(); loadProjects(); }, [loadItems, loadProjects]);
  useEffect(() => {
    contentItemsAPI.getStandalone().then(setStandalones).catch(() => {});
    purchaseOrdersAPI.getAllSelectable().then(setPos).catch(() => {});
  }, []);

  const openCreate = () => { setForm(emptyForm); setSelectedCatalogCourse(null); setCustomFieldKeys([]); setCalculatedGoLive(''); setDuplicateTitleError(''); setSimilarSuggestions([]); setSuggestionDismissed(false); setCatalogDialogOpen(true); };

  const handleCatalogSelect = (course) => {
    setCatalogDialogOpen(false);
    setSelectedCatalogCourse(course);
    setForm({ ...emptyForm, title: course?.title || '', solution_area: course?.solution_area?.[0] || '' });
    setCustomFieldKeys([]);
    setCalculatedGoLive('');
    setDuplicateTitleError('');
    setSimilarSuggestions([]);
    setSuggestionDismissed(false);
    setDialogOpen(true);
  };

  useEffect(() => {
    if (!dialogOpen || !form.title || form.title.trim().length < 4) { setSimilarSuggestions([]); return; }
    setSuggestionDismissed(false);
    const t = setTimeout(() => {
      contentItemsAPI.getSimilarTitles(form.title.trim()).then(setSimilarSuggestions).catch(() => {});
    }, 400);
    return () => clearTimeout(t);
  }, [dialogOpen, form.title]);

  useEffect(() => {
    if (!dialogOpen || !form.po_receipt_date || !form.workback_template_id) { setCalculatedGoLive(''); return; }
    const t = setTimeout(() => {
      workbackTemplateAPI.calculateGoLive(form.workback_template_id, form.po_receipt_date)
        .then(res => {
          setCalculatedGoLive(res.go_live_date);
          setForm(f => (f.target_go_live_date && f.target_go_live_date !== res.go_live_date && f._userEditedGoLive) ? f : { ...f, target_go_live_date: res.go_live_date });
        })
        .catch(() => {});
    }, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dialogOpen, form.po_receipt_date, form.workback_template_id]);

  const handleGoLiveDateChange = (value) => {
    setForm(f => ({ ...f, target_go_live_date: value, _userEditedGoLive: true, go_live_override_reason: value !== calculatedGoLive ? f.go_live_override_reason : '' }));
  };

  const handleSave = async () => {
    if (!form.title || !form.technology_area || !form.content_track || !form.build_type || !form.asset_type) {
      toast.error('Please fill in title, featured workload, readiness stage, asset type and build type'); return;
    }
    if (form.build_type !== 'new_standalone' && !form.parent_item_id) {
      toast.error('Please select the parent standalone course'); return;
    }
    if (calculatedGoLive && form.target_go_live_date !== calculatedGoLive && !form.go_live_override_reason?.trim()) {
      toast.error('Please add a reason for changing the auto-calculated go-live date'); return;
    }
    setSaving(true);
    try {
      const payload = {
        title: form.title,
        technology_area: form.technology_area === 'Other' ? form.technology_area_other : form.technology_area,
        solution_area: form.solution_area || null,
        asset_type: form.asset_type,
        content_track: form.content_track,
        build_type: form.build_type,
        parent_item_id: form.build_type !== 'new_standalone' ? form.parent_item_id : null,
        ms_priority: form.ms_priority || null,
        content_po_id: form.content_po_id || null,
        po_amount: form.po_amount ? parseFloat(form.po_amount) : null,
        po_receipt_date: form.po_receipt_date || null,
        workback_template_id: form.workback_template_id || null,
        target_go_live_date: form.target_go_live_date || null,
        calculated_go_live_date: calculatedGoLive || null,
        go_live_override_reason: form.go_live_override_reason || null,
        catalog_course_id: selectedCatalogCourse?.course_id || null,
        custom_fields: Object.keys(form.custom_fields || {}).length ? form.custom_fields : null,
        project_id: form.project_id || null,
      };
      const created = await contentItemsAPI.create(payload);
      toast.success('Content item created');
      setDialogOpen(false);
      navigate(`/content-items/${created.id}`);
    } catch (e) {
      const detail = e.response?.data?.detail || 'Failed to create content item';
      toast.error(detail);
      if (detail.toLowerCase().includes('already exists')) setDuplicateTitleError(detail);
    } finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await contentItemsAPI.delete(deleteTarget.id);
      toast.success(`"${deleteTarget.title}" deleted`);
      setDeleteTarget(null);
      loadItems();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete content item');
    } finally { setDeleting(false); }
  };

  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImportLoading(true);
    setImportResult(null);
    try {
      const result = await contentItemsAPI.bulkImport(file);
      setImportResult(result);
      if (result.created > 0) { loadItems(); toast.success(result.message); }
    } catch { toast.error('Import failed — check the file format'); }
    finally { setImportLoading(false); e.target.value = ''; }
  };

  const filtered = items.filter(i => {
    if (search && !i.title.toLowerCase().includes(search.toLowerCase()) && !(i.technology_area || '').toLowerCase().includes(search.toLowerCase())) return false;
    if (catalogFilter === 'linked' && !i.catalog_course_id) return false;
    if (catalogFilter === 'unlinked' && i.catalog_course_id) return false;
    if (deliveryFilter === 'linked' && !i.linked_deliveries_count) return false;
    if (deliveryFilter === 'unlinked' && i.linked_deliveries_count) return false;
    if (colFilters.technology_area.size && !colFilters.technology_area.has(i.technology_area)) return false;
    if (colFilters.solution_area.size && !colFilters.solution_area.has(i.solution_area)) return false;
    if (colFilters.asset_type.size && !colFilters.asset_type.has(i.asset_type)) return false;
    if (colFilters.content_track.size && !colFilters.content_track.has(i.content_track)) return false;
    if (colFilters.build_type.size && !colFilters.build_type.has(i.build_type)) return false;
    if (colFilters.ms_priority.size && !colFilters.ms_priority.has(i.ms_priority)) return false;
    if (colFilters.catalog_status.size && !colFilters.catalog_status.has(i.catalog_status)) return false;
    if (colFilters.project.size && !colFilters.project.has(i.project_name || '')) return false;
    if (availabilityFilter && i.availability_projection !== availabilityFilter) return false;
    if (flagFilter === 'triage' && !i.needs_triage) return false;
    if (flagFilter === 'slippage' && !(i.ms_priority === 'P1' && ['at_risk', 'overdue'].includes(i.availability_projection))) return false;
    for (const [tid, set] of Object.entries(customColFilters)) {
      if (!set || !set.size) continue;
      const raw = i.custom_fields?.[tid];
      const vals = Array.isArray(raw) ? raw : (raw ? [raw] : []);
      if (!vals.some(v => set.has(v))) return false;
    }
    return true;
  }).sort((a, b) => {
    if (!sortBy) return 0;
    let av = a[sortBy] ?? '';
    let bv = b[sortBy] ?? '';
    if (typeof av === 'string') av = av.toLowerCase();
    if (typeof bv === 'string') bv = bv.toLowerCase();
    if (av < bv) return sortDir === 'asc' ? -1 : 1;
    if (av > bv) return sortDir === 'asc' ? 1 : -1;
    return 0;
  });

  const uniqOpts = (key) => [...new Set(items.map(i => i[key]).filter(Boolean))].sort().map(v => ({ value: v, label: v }));
  const twOpts = uniqOpts('technology_area');
  const saOpts = uniqOpts('solution_area');
  const atOpts = uniqOpts('asset_type');
  const btOpts = uniqOpts('build_type');
  const trackOpts = readinessOptions.filter(o => items.some(i => i.content_track === o.value)).map(o => ({ value: o.value, label: o.label }));
  const priorityColOpts = [...new Set(items.map(i => i.ms_priority).filter(Boolean))].sort().map(v => ({ value: v, label: v }));
  const catStatusOpts = Object.entries(CATALOG_STATUS_STYLES).filter(([k]) => items.some(i => i.catalog_status === k)).map(([k, v]) => ({ value: k, label: v.label }));
  const projectColOpts = [...new Set(items.map(i => i.project_name).filter(Boolean))].sort().map(v => ({ value: v, label: v }));
  const projMsOwnerOpts = [...new Set(projects.map(p => p.ms_owner).filter(Boolean))].sort().map(v => ({ value: v, label: msOwnerOptions.find(o => o.value === v)?.label || v }));
  const projPmOwnerOpts = [...new Set(projects.map(p => p.pm_owner).filter(Boolean))].sort().map(v => ({ value: v, label: pmOwnerOptions.find(o => o.value === v)?.label || v }));
  const projPoOpts = [...new Set(projects.map(p => p.linked_po_number).filter(Boolean))].sort().map(v => ({ value: v, label: v }));
  const customColOptsFor = (c) => (c.options || []).map(o => ({ value: o.value ?? o, label: o.label ?? o }));

  useEffect(() => { setContentPage(1); }, [search, catalogFilter, deliveryFilter, colFilters, customColFilters, sortBy, sortDir, availabilityFilter, flagFilter]);
  useEffect(() => { setProjPage(1); }, [projSearch, projColFilters]);
  const pagedContent = filtered.slice((contentPage - 1) * PAGE_SIZE, contentPage * PAGE_SIZE);

  return (
    <div className="space-y-6 animate-fade-in" data-testid="content-items-page">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-heading font-bold">ContentDev Tracker</h1>
          <p className="text-muted-foreground">Track every content build, refresh and new-SKU activity end-to-end</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {tab === 'tasks' && (<>
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" size="sm" data-testid="columns-picker-btn">
                <Columns3 className="w-4 h-4 mr-2" />Columns
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-64" align="end" data-testid="columns-picker-popover">
              <p className="text-xs font-medium text-muted-foreground mb-2">Show/hide columns</p>
              <div className="space-y-2 max-h-72 overflow-y-auto">
                {[...ALL_COLUMNS, ...contentCustomColumns].map(c => (
                  <label key={c.key} className="flex items-center gap-2 text-sm cursor-pointer" data-testid={`column-toggle-${c.key}`}>
                    <Checkbox checked={isColVisible(c.key)} onCheckedChange={() => toggleColumn(c.key)} />
                    {c.label}
                  </label>
                ))}
              </div>
            </PopoverContent>
          </Popover>
          <Button variant="outline" size="sm" onClick={() => contentItemsAPI.downloadTemplate()} data-testid="content-items-download-template-btn">
            <Download className="w-4 h-4 mr-2" />Template
          </Button>
          <Button variant="outline" size="sm" onClick={() => setImportOpen(true)} data-testid="content-items-bulk-import-btn">
            <Upload className="w-4 h-4 mr-2" />Bulk Import
          </Button>
          <Button variant="outline" size="sm" onClick={() => contentItemsAPI.exportTracker()} data-testid="content-items-export-btn">
            <FileSpreadsheet className="w-4 h-4 mr-2" />Export Tracker
          </Button>
          <Button onClick={openCreate} data-testid="new-content-item-btn" className="bg-lime text-lime-foreground hover:bg-lime/90">
            <Plus className="w-4 h-4 mr-2" />New Content Item
          </Button>
          </>)}
          {tab === 'projects' && (
            <Button onClick={() => { setEditProject(null); setProjectDialogOpen(true); }} data-testid="new-content-project-btn" className="bg-lime text-lime-foreground hover:bg-lime/90">
              <Plus className="w-4 h-4 mr-2" />New Project
            </Button>
          )}
        </div>
      </div>

      <div className="flex items-center gap-1 border-b border-border" data-testid="contentdev-tabs">
        <button
          onClick={() => { setTab('tasks'); setSearchParams({}); }}
          className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors flex items-center gap-1.5 ${tab === 'tasks' ? 'border-lime text-foreground' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
          data-testid="contentdev-tab-tasks"
        >
          <ListChecks className="w-4 h-4" />Tasks <span className="text-[10px] bg-muted rounded-full px-1.5">{items.length}</span>
        </button>
        <button
          onClick={() => { setTab('projects'); setSearchParams({ tab: 'projects' }); }}
          className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors flex items-center gap-1.5 ${tab === 'projects' ? 'border-lime text-foreground' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
          data-testid="contentdev-tab-projects"
        >
          <FolderKanban className="w-4 h-4" />Projects <span className="text-[10px] bg-muted rounded-full px-1.5">{projects.length}</span>
        </button>
      </div>

      {tab === 'tasks' && (<>

      {(availabilityFilter || flagFilter) && (
        <div className="flex items-center justify-between gap-3 bg-amber-50 border border-amber-200 rounded-lg px-4 py-2 mb-4" data-testid="availability-filter-banner">
          <p className="text-sm text-amber-800">
            {availabilityFilter && <>Showing items with availability: <span className="font-semibold">{{ on_track: 'On Track', at_risk: 'At Risk', overdue: 'Overdue', no_target_set: 'No Target Set' }[availabilityFilter] || availabilityFilter}</span></>}
            {flagFilter === 'triage' && <>Showing items <span className="font-semibold">flagged for triage</span></>}
            {flagFilter === 'slippage' && <>Showing <span className="font-semibold">P1 items at risk of slippage</span></>}
          </p>
          <Button size="sm" variant="ghost" className="h-7 text-amber-700 hover:bg-amber-100" onClick={() => { setAvailabilityFilter(''); setFlagFilter(''); setSearchParams({}); }} data-testid="clear-availability-filter-btn">
            <X className="w-3.5 h-3.5 mr-1.5" />Clear
          </Button>
        </div>
      )}

      <Card>
        <CardContent className="pt-5">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input placeholder="Search title or technology..." value={search} onChange={e => setSearch(e.target.value)} className="pl-10" data-testid="content-items-search" />
            </div>
            <Select value={catalogFilter} onValueChange={setCatalogFilter}>
              <SelectTrigger className="w-full sm:w-44" data-testid="content-items-catalog-filter"><SelectValue placeholder="All Catalog Link" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All (Catalog Link)</SelectItem>
                <SelectItem value="linked">Linked to Catalog</SelectItem>
                <SelectItem value="unlinked">Not Linked</SelectItem>
              </SelectContent>
            </Select>
            <Select value={deliveryFilter} onValueChange={setDeliveryFilter}>
              <SelectTrigger className="w-full sm:w-48" data-testid="content-items-delivery-filter"><SelectValue placeholder="All Delivery Link" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All (Delivery Link)</SelectItem>
                <SelectItem value="linked">Has Linked Delivery</SelectItem>
                <SelectItem value="unlinked">No Delivery Linked Yet</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      <ActiveFiltersBar
        filters={[
          { key: 'technology_area', label: 'Featured Workload', set: colFilters.technology_area, options: twOpts, onChange: (s) => setColFilter('technology_area', s) },
          { key: 'solution_area', label: 'Solution Area', set: colFilters.solution_area, options: saOpts, onChange: (s) => setColFilter('solution_area', s) },
          { key: 'asset_type', label: 'Asset Type', set: colFilters.asset_type, options: atOpts, onChange: (s) => setColFilter('asset_type', s) },
          { key: 'content_track', label: 'Readiness Stage', set: colFilters.content_track, options: trackOpts, onChange: (s) => setColFilter('content_track', s) },
          { key: 'build_type', label: 'Build Type', set: colFilters.build_type, options: btOpts, onChange: (s) => setColFilter('build_type', s) },
          { key: 'ms_priority', label: 'MS Priority', set: colFilters.ms_priority, options: priorityColOpts, onChange: (s) => setColFilter('ms_priority', s) },
          { key: 'catalog_status', label: 'Catalog Status', set: colFilters.catalog_status, options: catStatusOpts, onChange: (s) => setColFilter('catalog_status', s) },
          { key: 'project', label: 'Project', set: colFilters.project, options: projectColOpts, onChange: (s) => setColFilter('project', s) },
          ...contentCustomColumns.map(c => ({ key: c.taxonomy_id, label: c.label, set: customColFilters[c.taxonomy_id] || new Set(), options: customColOptsFor(c), onChange: (s) => setCustomColFilter(c.taxonomy_id, s) })),
        ]}
        search={search}
        onClearSearch={() => setSearch('')}
        testId="content-active-filters"
      />

      {selectedIds.size > 0 && (
        <div className="flex items-center justify-between bg-lime/10 border border-lime/30 rounded-lg px-4 py-2.5" data-testid="bulk-edit-toolbar">
          <span className="text-sm font-medium">{selectedIds.size} item{selectedIds.size !== 1 ? 's' : ''} selected</span>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" onClick={() => setBulkEditOpen(true)} data-testid="open-bulk-edit-btn">
              <PencilLine className="w-3.5 h-3.5 mr-1.5" />Bulk Edit
            </Button>
            <Button size="sm" variant="outline" onClick={() => { setEditProject(null); setProjectFromSelection(true); setProjectDialogOpen(true); }} data-testid="create-project-from-selection-btn">
              <FolderKanban className="w-3.5 h-3.5 mr-1.5" />Create Project from Selection
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setSelectedIds(new Set())} data-testid="clear-selection-btn">
              <X className="w-3.5 h-3.5 mr-1.5" />Clear
            </Button>
          </div>
        </div>
      )}

      <Card>
        <CardContent className="pt-5">
          {loading ? (
            <div className="space-y-3">{[1, 2, 3].map(i => <div key={i} className="h-12 bg-muted animate-pulse rounded" />)}</div>
          ) : filtered.length > 0 ? (
            <Table containerClassName="max-h-[calc(100vh-320px)]">
              <TableHeader sticky>
                <TableRow>
                  <TableHead className="w-10">
                    <Checkbox checked={selectedIds.size > 0 && selectedIds.size === filtered.length} onCheckedChange={toggleSelectAll} data-testid="select-all-checkbox" />
                  </TableHead>
                  <TableHead><SortHeader sortKey="title">Course</SortHeader></TableHead>
                  {isColVisible('featured_workload') && <TableHead><ColumnFilterPopover label="Featured Workload" options={twOpts} selected={colFilters.technology_area} onChange={(s) => setColFilter('technology_area', s)} testId="content-col-filter-workload" /></TableHead>}
                  {isColVisible('solution_area') && <TableHead><ColumnFilterPopover label="Solution Area" options={saOpts} selected={colFilters.solution_area} onChange={(s) => setColFilter('solution_area', s)} testId="content-col-filter-solution" /></TableHead>}
                  {isColVisible('asset_type') && <TableHead><ColumnFilterPopover label="Asset Type" options={atOpts} selected={colFilters.asset_type} onChange={(s) => setColFilter('asset_type', s)} testId="content-col-filter-asset" /></TableHead>}
                  {isColVisible('readiness_stage') && <TableHead><ColumnFilterPopover label="Readiness Stage" options={trackOpts} selected={colFilters.content_track} onChange={(s) => setColFilter('content_track', s)} testId="content-col-filter-readiness" /></TableHead>}
                  {isColVisible('build_type') && <TableHead><ColumnFilterPopover label="Build Type" options={btOpts} selected={colFilters.build_type} onChange={(s) => setColFilter('build_type', s)} testId="content-col-filter-build" /></TableHead>}
                  {isColVisible('ms_priority') && <TableHead><ColumnFilterPopover label="MS Priority" options={priorityColOpts} selected={colFilters.ms_priority} onChange={(s) => setColFilter('ms_priority', s)} testId="content-col-filter-priority" /></TableHead>}
                  {isColVisible('project') && <TableHead><ColumnFilterPopover label="Project" options={projectColOpts} selected={colFilters.project} onChange={(s) => setColFilter('project', s)} testId="content-col-filter-project" /></TableHead>}
                  {isColVisible('current_phase') && <TableHead>Current Phase</TableHead>}
                  {isColVisible('catalog_status') && <TableHead><ColumnFilterPopover label="Catalog Status" options={catStatusOpts} selected={colFilters.catalog_status} onChange={(s) => setColFilter('catalog_status', s)} testId="content-col-filter-catalog-status" /></TableHead>}
                  {isColVisible('catalog_link') && <TableHead>Catalog Link</TableHead>}
                  {isColVisible('ms_course_id') && <TableHead>MS Course ID</TableHead>}
                  {isColVisible('target_go_live') && <TableHead>Target Go-Live</TableHead>}
                  {isColVisible('po_amount') && <TableHead>PO Amount</TableHead>}
                  {isColVisible('linked_deliveries') && <TableHead>Linked Deliveries</TableHead>}
                  {contentCustomColumns.filter(c => isColVisible(c.key)).map(c => (
                    <TableHead key={c.key}><ColumnFilterPopover label={c.label} options={customColOptsFor(c)} selected={customColFilters[c.taxonomy_id] || new Set()} onChange={(s) => setCustomColFilter(c.taxonomy_id, s)} testId={`content-col-filter-${c.key}`} /></TableHead>
                  ))}
                  <TableHead className="w-10"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {pagedContent.map(item => {
                  const BuildIcon = BUILD_TYPE_ICON[item.build_type] || BookOpen;
                  return (
                    <TableRow key={item.id} className="cursor-pointer hover:bg-muted/50" data-testid={`content-item-row-${item.id}`} onClick={() => navigate(`/content-items/${item.id}`)}>
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <Checkbox checked={selectedIds.has(item.id)} onCheckedChange={() => toggleSelectRow(item.id)} data-testid={`select-row-${item.id}`} />
                      </TableCell>
                      <TableCell className="font-medium text-sm max-w-[240px] truncate">{item.title}</TableCell>
                      {isColVisible('featured_workload') && <TableCell><Badge variant="outline" className="text-xs">{item.technology_area}</Badge></TableCell>}
                      {isColVisible('solution_area') && <TableCell className="text-sm">{item.solution_area || <span className="text-xs text-muted-foreground">—</span>}</TableCell>}
                      {isColVisible('asset_type') && <TableCell className="text-sm">{item.asset_type || <span className="text-xs text-muted-foreground">—</span>}</TableCell>}
                      {isColVisible('readiness_stage') && <TableCell className="text-sm">{readinessLabel(item.content_track)}</TableCell>}
                      {isColVisible('build_type') && (
                        <TableCell>
                          <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
                            <BuildIcon className="w-3.5 h-3.5" />
                            {buildTypeOptions.find(b => b.value === item.build_type)?.label}
                          </span>
                        </TableCell>
                      )}
                      {isColVisible('ms_priority') && <TableCell>{item.ms_priority ? <Badge className={MS_PRIORITY_STYLES[item.ms_priority]}>{item.ms_priority}</Badge> : <span className="text-xs text-muted-foreground">—</span>}</TableCell>}
                      {isColVisible('project') && (
                        <TableCell className="text-sm" data-testid={`content-item-project-cell-${item.id}`}>
                          {item.project_name ? (
                            <button
                              onClick={(e) => { e.stopPropagation(); navigate(`/content-projects/${item.project_id}`); }}
                              className="flex items-center gap-1 text-lime-700 hover:underline"
                              data-testid={`content-item-project-link-${item.id}`}
                            >
                              <FolderKanban className="w-3 h-3" />{item.project_name}
                            </button>
                          ) : <span className="text-xs text-muted-foreground">—</span>}
                        </TableCell>
                      )}
                      {isColVisible('current_phase') && <TableCell className="text-sm">{item.current_phase?.label}</TableCell>}
                      {isColVisible('catalog_status') && <TableCell><Badge className={CATALOG_STATUS_STYLES[item.catalog_status]?.bg}>{CATALOG_STATUS_STYLES[item.catalog_status]?.label}</Badge></TableCell>}
                      {isColVisible('catalog_link') && (
                        <TableCell>
                          {item.catalog_course_id ? (
                            <Badge className="bg-emerald-100 text-emerald-700 flex items-center gap-1 w-fit" data-testid={`catalog-linked-badge-${item.id}`}>
                              <Link2 className="w-3 h-3" />Linked
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="text-muted-foreground w-fit">Not Linked</Badge>
                          )}
                        </TableCell>
                      )}
                      {isColVisible('ms_course_id') && <TableCell className="text-xs font-mono">{item.catalog_ms_course_id || <span className="text-muted-foreground">—</span>}</TableCell>}
                      {isColVisible('target_go_live') && (
                        <TableCell className="text-sm">
                          {item.target_go_live_date ? (
                            <div className="flex flex-col gap-0.5">
                              <span className={`flex items-center gap-1 ${item.availability_projection === 'overdue' ? 'text-red-600' : item.availability_projection === 'at_risk' ? 'text-amber-600' : 'text-muted-foreground'}`}>
                                <CalendarClock className="w-3 h-3" />{item.target_go_live_date}
                              </span>
                              {item.go_live_slipped_days > 0 && (
                                <Badge className="bg-red-100 text-red-700 text-[10px] w-fit flex items-center gap-1" data-testid={`go-live-slipped-badge-${item.id}`}>
                                  <AlertCircle className="w-3 h-3" />Slipped {item.go_live_slipped_days}d
                                </Badge>
                              )}
                            </div>
                          ) : '—'}
                        </TableCell>
                      )}
                      {isColVisible('po_amount') && <TableCell className="text-sm">{item.po_amount ? <span className="flex items-center gap-1"><DollarSign className="w-3 h-3 text-muted-foreground" />{item.po_amount.toLocaleString()}</span> : '—'}</TableCell>}
                      {isColVisible('linked_deliveries') && (
                        <TableCell className="text-sm" data-testid={`linked-deliveries-cell-${item.id}`}>
                          {item.linked_deliveries_count > 0 ? (
                            <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">{item.linked_deliveries_count} linked</Badge>
                          ) : <span className="text-xs text-muted-foreground">None</span>}
                        </TableCell>
                      )}
                      {contentCustomColumns.filter(c => isColVisible(c.key)).map(c => (
                        <TableCell key={c.key} className="text-sm">{renderCustomValue(item.custom_fields?.[c.taxonomy_id], c.options) || <span className="text-xs text-muted-foreground">—</span>}</TableCell>
                      ))}
                      <TableCell>
                        <button
                          onClick={(e) => { e.stopPropagation(); setDeleteTarget(item); }}
                          className="text-muted-foreground hover:text-red-600 transition-colors p-1"
                          data-testid={`content-item-delete-btn-${item.id}`}
                          title="Delete content item"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          ) : (
            <div className="text-center py-16">
              <Layers className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-medium mb-2">No content items found</h3>
              <p className="text-muted-foreground mb-4">Create your first content build to start tracking its lifecycle</p>
              <Button onClick={openCreate} className="bg-lime text-lime-foreground hover:bg-lime/90"><Plus className="w-4 h-4 mr-2" />New Content Item</Button>
            </div>
          )}
          <TablePagination page={contentPage} total={filtered.length} onPageChange={setContentPage} testId="content-pagination" itemLabel="items" />
        </CardContent>
      </Card>
      </>)}

      {tab === 'projects' && (
        <Card data-testid="content-projects-section">
          <CardContent className="pt-5">
            <div className="flex items-center gap-2 mb-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input placeholder="Search projects..." value={projSearch} onChange={e => { setProjSearch(e.target.value); setProjPage(1); }} className="pl-10" data-testid="content-projects-search" />
              </div>
              <Button
                variant={projOverdueOnly ? 'default' : 'outline'}
                size="sm"
                onClick={() => { setProjOverdueOnly(v => !v); setProjPage(1); }}
                className={projOverdueOnly ? 'bg-red-600 hover:bg-red-700 text-white' : 'text-red-600 border-red-200 hover:bg-red-50'}
                data-testid="projects-overdue-filter-btn"
              >
                <AlertCircle className="w-3.5 h-3.5 mr-1.5" />Overdue only
                {(() => { const n = projects.filter(isProjectOverdue).length; return n > 0 ? <span className="ml-1.5 text-[10px] bg-white/20 rounded-full px-1.5">{n}</span> : null; })()}
              </Button>
            </div>
            <ActiveFiltersBar
              filters={[
                { key: 'ms_owner', label: 'MS Owner', set: projColFilters.ms_owner, options: projMsOwnerOpts, onChange: (s) => setProjColFilter('ms_owner', s) },
                { key: 'pm_owner', label: 'PM Owner', set: projColFilters.pm_owner, options: projPmOwnerOpts, onChange: (s) => setProjColFilter('pm_owner', s) },
                { key: 'linked_po_number', label: 'PO Number', set: projColFilters.linked_po_number, options: projPoOpts, onChange: (s) => setProjColFilter('linked_po_number', s) },
              ]}
              search={projSearch}
              onClearSearch={() => setProjSearch('')}
              testId="content-projects-active-filters"
            />
            {(() => {
              const fp = projects.filter(p => {
                if (projSearch && !(p.name || '').toLowerCase().includes(projSearch.toLowerCase())) return false;
                if (projColFilters.ms_owner.size && !projColFilters.ms_owner.has(p.ms_owner)) return false;
                if (projColFilters.pm_owner.size && !projColFilters.pm_owner.has(p.pm_owner)) return false;
                if (projColFilters.linked_po_number.size && !projColFilters.linked_po_number.has(p.linked_po_number)) return false;
                if (projOverdueOnly && !isProjectOverdue(p)) return false;
                return true;
              });
              const paged = fp.slice((projPage - 1) * PAGE_SIZE, projPage * PAGE_SIZE);
              if (fp.length === 0) {
                const hasProjects = projects.length > 0;
                return (
                  <div className="text-center py-16">
                    <FolderKanban className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                    <h3 className="text-lg font-medium mb-2">{hasProjects ? 'No projects match your filters' : 'No projects yet'}</h3>
                    <p className="text-muted-foreground mb-4">{hasProjects ? 'Try clearing the search or column filters above.' : 'Create a project to group related content items together.'}</p>
                    {!hasProjects && <Button onClick={() => { setEditProject(null); setProjectDialogOpen(true); }} className="bg-lime text-lime-foreground hover:bg-lime/90" data-testid="projects-empty-new-btn"><Plus className="w-4 h-4 mr-2" />New Project</Button>}
                  </div>
                );
              }
              return (
                <>
                  <Table containerClassName="max-h-[calc(100vh-340px)]">
                    <TableHeader sticky>
                      <TableRow>
                        <TableHead>Project</TableHead>
                        <TableHead><ColumnFilterPopover label="MS Owner" options={projMsOwnerOpts} selected={projColFilters.ms_owner} onChange={(s) => setProjColFilter('ms_owner', s)} testId="proj-col-filter-msowner" /></TableHead>
                        <TableHead><ColumnFilterPopover label="PM Owner" options={projPmOwnerOpts} selected={projColFilters.pm_owner} onChange={(s) => setProjColFilter('pm_owner', s)} testId="proj-col-filter-pmowner" /></TableHead>
                        <TableHead>Target Delivery</TableHead>
                        <TableHead><ColumnFilterPopover label="PO Number" options={projPoOpts} selected={projColFilters.linked_po_number} onChange={(s) => setProjColFilter('linked_po_number', s)} testId="proj-col-filter-po" /></TableHead>
                        <TableHead>Item Status</TableHead>
                        <TableHead>Items</TableHead>
                        <TableHead>Created</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {paged.map(p => (
                        <TableRow key={p.id} className={`cursor-pointer hover:bg-muted/50 ${isProjectOverdue(p) ? 'bg-red-50/40' : ''}`} data-testid={`content-project-row-${p.id}`} onClick={() => navigate(`/content-projects/${p.id}`)}>
                          <TableCell className="font-medium text-sm max-w-[260px]">
                            <div className="flex items-center gap-2"><FolderKanban className="w-4 h-4 text-lime-600 flex-shrink-0" /><span className="truncate">{p.name}</span>
                              {isProjectOverdue(p) && <Badge className="bg-red-100 text-red-700 border-red-200 text-[10px] flex-shrink-0" data-testid={`content-project-overdue-${p.id}`}>Overdue</Badge>}
                            </div>
                            {p.description && <p className="text-xs text-muted-foreground truncate mt-0.5">{p.description}</p>}
                          </TableCell>
                          <TableCell className="text-sm">{msOwnerOptions.find(o => o.value === p.ms_owner)?.label || p.ms_owner || <span className="text-xs text-muted-foreground">—</span>}</TableCell>
                          <TableCell className="text-sm">{pmOwnerOptions.find(o => o.value === p.pm_owner)?.label || p.pm_owner || <span className="text-xs text-muted-foreground">—</span>}</TableCell>
                          <TableCell className="text-sm">{p.target_delivery_date ? <span className="flex items-center gap-1"><CalendarClock className="w-3 h-3 text-muted-foreground" />{p.target_delivery_date}</span> : <span className="text-xs text-muted-foreground">—</span>}</TableCell>
                          <TableCell className="text-sm">{p.linked_po_number ? <span className="flex items-center gap-1"><Receipt className="w-3 h-3 text-muted-foreground" />{p.linked_po_number}</span> : <span className="text-xs text-muted-foreground">—</span>}</TableCell>
                          <TableCell><ProjectStatusBar rollup={p.status_rollup} testId={`content-project-status-${p.id}`} /></TableCell>
                          <TableCell><Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200" data-testid={`content-project-count-${p.id}`}>{p.item_count}</Badge></TableCell>
                          <TableCell className="text-xs text-muted-foreground">{p.created_at ? new Date(p.created_at).toLocaleDateString() : '—'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  <TablePagination page={projPage} total={fp.length} onPageChange={setProjPage} testId="content-projects-pagination" itemLabel="projects" />
                </>
              );
            })()}
          </CardContent>
        </Card>
      )}

      <CatalogLinkDialog open={catalogDialogOpen} onOpenChange={setCatalogDialogOpen} onSelect={handleCatalogSelect} />

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="content-item-form-dialog">
          <DialogHeader>
            <DialogTitle>New Content Item</DialogTitle>
            <DialogDescription>Kick off the 7-phase content build lifecycle</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {selectedCatalogCourse ? (
              <div className="flex items-center justify-between gap-3 border border-lime/40 bg-lime/5 rounded-lg p-3" data-testid="selected-catalog-course-card">
                <div className="min-w-0 flex items-center gap-2">
                  <Link2 className="w-4 h-4 text-lime-600 flex-shrink-0" />
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate">{selectedCatalogCourse.title}</p>
                    <p className="text-xs text-muted-foreground">Linked to Catalog · {(selectedCatalogCourse.solution_area || []).join(', ')}</p>
                  </div>
                </div>
                <button type="button" onClick={() => { setSelectedCatalogCourse(null); setDialogOpen(false); setCatalogDialogOpen(true); }} className="text-xs text-lime-600 hover:underline flex-shrink-0" data-testid="change-catalog-link-btn">
                  Change
                </button>
              </div>
            ) : (
              <button type="button" onClick={() => { setDialogOpen(false); setCatalogDialogOpen(true); }} className="w-full flex items-center justify-center gap-2 border border-dashed border-border text-xs text-muted-foreground rounded-lg py-2 hover:border-lime/40 transition-colors" data-testid="link-catalog-from-form-btn">
                <LinkIcon className="w-3.5 h-3.5" />Not linked to Catalog — search again
              </button>
            )}
            <div>
              <Label>Course Title</Label>
              <Input value={form.title} onChange={e => { setForm({ ...form, title: e.target.value }); setDuplicateTitleError(''); }} placeholder="e.g. Microsoft Fabric for Data Engineers" data-testid="content-item-title-input" />
              {duplicateTitleError && <p className="text-xs text-red-600 mt-1" data-testid="content-item-title-duplicate-error">{duplicateTitleError}</p>}
              {!duplicateTitleError && !suggestionDismissed && similarSuggestions.length > 0 && (
                <div className="mt-1.5 flex items-start justify-between gap-2 bg-amber-50 border border-amber-200 rounded-md px-2.5 py-1.5" data-testid="similar-title-suggestion">
                  <p className="text-xs text-amber-800">
                    Did you mean to update{' '}
                    <button
                      type="button"
                      className="underline font-medium"
                      onClick={() => { setDialogOpen(false); navigate(`/content-items/${similarSuggestions[0].id}`); }}
                      data-testid="similar-title-suggestion-link"
                    >
                      "{similarSuggestions[0].title}"
                    </button>{' '}
                    instead?
                  </p>
                  <button type="button" onClick={() => setSuggestionDismissed(true)} className="text-amber-600 hover:text-amber-800 flex-shrink-0" data-testid="dismiss-similar-title-suggestion">
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              )}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Featured Workload</Label>
                <Select value={form.technology_area} onValueChange={v => setForm({ ...form, technology_area: v })}>
                  <SelectTrigger data-testid="content-item-technology-select"><SelectValue placeholder="Select..." /></SelectTrigger>
                  <SelectContent>{featuredWorkloadOptions.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                </Select>
                {form.technology_area === 'Other' && (
                  <Input className="mt-2" placeholder="Specify technology" value={form.technology_area_other} onChange={e => setForm({ ...form, technology_area_other: e.target.value })} data-testid="content-item-technology-other-input" />
                )}
              </div>
              <div>
                <Label>Readiness Stage</Label>
                <Select value={form.content_track} onValueChange={v => setForm({ ...form, content_track: v })}>
                  <SelectTrigger data-testid="content-item-track-select"><SelectValue placeholder="Select..." /></SelectTrigger>
                  <SelectContent>{readinessOptions.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Solution Area</Label>
                <Select value={form.solution_area} onValueChange={v => setForm({ ...form, solution_area: v })}>
                  <SelectTrigger data-testid="content-item-solution-area-select"><SelectValue placeholder="Optional" /></SelectTrigger>
                  <SelectContent>{solutionAreaOptions.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Asset Type</Label>
                <Select value={form.asset_type} onValueChange={v => setForm({ ...form, asset_type: v })}>
                  <SelectTrigger data-testid="content-item-asset-type-select"><SelectValue placeholder="Select..." /></SelectTrigger>
                  <SelectContent>{assetTypeOptions.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Build Type</Label>
                <Select value={form.build_type} onValueChange={v => setForm({ ...form, build_type: v, parent_item_id: '' })}>
                  <SelectTrigger data-testid="content-item-buildtype-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{buildTypeOptions.map(b => <SelectItem key={b.value} value={b.value}>{b.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>MS Priority</Label>
                <Select value={form.ms_priority} onValueChange={v => setForm({ ...form, ms_priority: v })}>
                  <SelectTrigger data-testid="content-item-priority-select"><SelectValue placeholder="Optional" /></SelectTrigger>
                  <SelectContent>{priorityOptions.map(p => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}</SelectContent>
                </Select>
                <p className="text-[11px] text-muted-foreground mt-1">Microsoft's budget allocation priority for this build/refresh</p>
              </div>
            </div>
            {form.build_type !== 'new_standalone' && (
              <div>
                <Label>Parent Standalone Course</Label>
                <Select value={form.parent_item_id} onValueChange={v => setForm({ ...form, parent_item_id: v })}>
                  <SelectTrigger data-testid="content-item-parent-select"><SelectValue placeholder="Select parent course..." /></SelectTrigger>
                  <SelectContent>{standalones.map(s => <SelectItem key={s.id} value={s.id}>{s.title}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            <div>
              <Label className="flex items-center gap-1.5"><FolderKanban className="w-3.5 h-3.5" />Project (optional)</Label>
              <Select value={form.project_id || '__none__'} onValueChange={v => setForm({ ...form, project_id: v === '__none__' ? '' : v })}>
                <SelectTrigger data-testid="content-item-project-select"><SelectValue placeholder="Standalone (no project)" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">— Standalone (no project) —</SelectItem>
                  {projects.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Content PO (Microsoft)</Label>
                <Select value={form.content_po_id} onValueChange={v => setForm({ ...form, content_po_id: v })}>
                  <SelectTrigger data-testid="content-item-po-select"><SelectValue placeholder="Select PO (optional)" /></SelectTrigger>
                  <SelectContent>{pos.map(p => <SelectItem key={p.id} value={p.id}><span className="flex items-center gap-2"><span>{p.display_label}</span><POKindTag kind={p.kind} /></span></SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>PO Amount ($)</Label>
                <Input type="number" value={form.po_amount} onChange={e => setForm({ ...form, po_amount: e.target.value })} placeholder="Chartered amount" data-testid="content-item-po-amount-input" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>PO Receipt Date</Label>
                <Input type="date" value={form.po_receipt_date} onChange={e => setForm({ ...form, po_receipt_date: e.target.value })} data-testid="content-item-po-receipt-date-input" />
              </div>
              <div>
                <Label className="flex items-center gap-1.5"><Wand2 className="w-3.5 h-3.5" />Workback Template</Label>
                <Select value={form.workback_template_id} onValueChange={v => setForm({ ...form, workback_template_id: v })}>
                  <SelectTrigger data-testid="content-item-workback-template-select"><SelectValue placeholder="Select template (optional)" /></SelectTrigger>
                  <SelectContent>
                    {rankWorkbackTemplates(workbackTemplates, form.asset_type, form.content_track).map(t => (
                      <SelectItem key={t.id} value={t.id} data-testid={`workback-template-option-${t.id}`}>
                        <span className="flex items-center gap-1.5">
                          {t.title}{t.is_default ? ' (Default)' : ''}
                          {t._suggested && <Badge className="bg-lime/20 text-lime-700 text-[9px]" data-testid={`suggested-badge-${t.id}`}>Suggested</Badge>}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Target Go-Live Date</Label>
              <Input type="date" value={form.target_go_live_date} onChange={e => handleGoLiveDateChange(e.target.value)} data-testid="content-item-target-date-input" />
              {calculatedGoLive && (
                <p className="text-[11px] text-muted-foreground mt-1" data-testid="calculated-go-live-hint">
                  Auto-calculated from the workback template: <span className="font-medium text-foreground">{calculatedGoLive}</span>. Edit above if needed.
                </p>
              )}
              {calculatedGoLive && form.target_go_live_date !== calculatedGoLive && (
                <div className="mt-2">
                  <Label className="text-xs">Reason for changing the calculated date</Label>
                  <Textarea rows={2} value={form.go_live_override_reason} onChange={e => setForm({ ...form, go_live_override_reason: e.target.value })} placeholder="e.g. MS review team confirmed a later slot" data-testid="go-live-override-reason-input" />
                </div>
              )}
            </div>
            <div>
              <Label className="flex items-center gap-1.5">Catalog Stage <Info className="w-3 h-3 text-muted-foreground" /></Label>
              <Select disabled>
                <SelectTrigger className="opacity-60 cursor-not-allowed" data-testid="content-item-catalog-stage-select"><SelectValue placeholder="Not available via Catalog API yet" /></SelectTrigger>
                <SelectContent><SelectItem value="_">—</SelectItem></SelectContent>
              </Select>
              <p className="text-[11px] text-muted-foreground mt-1">The Catalog app doesn't expose its build stage via API yet — this will populate automatically once it does.</p>
            </div>

            <div className="pt-2 border-t border-border">
              <p className="text-xs font-medium text-muted-foreground mb-3">Additional Taxonomy Fields</p>
              <EntityCustomFields
                entityType="content_item"
                values={form.custom_fields || {}}
                onChange={(cf) => setForm({ ...form, custom_fields: cf })}
                isSuperAdmin={superAdmin}
                currentUser={user}
                onSchemaChanged={loadContentFields}
                testPrefix="content-custom-field"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving} data-testid="content-item-save-btn" className="bg-lime text-lime-foreground hover:bg-lime/90">
              {saving ? 'Creating...' : 'Create Content Item'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Import Dialog */}
      <input ref={importFileRef} type="file" accept=".xlsx,.xlsm" className="hidden" onChange={handleImport} data-testid="content-items-import-file-input" />
      <Dialog open={importOpen} onOpenChange={(o) => { setImportOpen(o); if (!o) setImportResult(null); }}>
        <DialogContent className="max-w-md" data-testid="content-items-import-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><FileSpreadsheet className="w-5 h-5 text-lime" />Bulk Import Content Items</DialogTitle>
            <DialogDescription>Upload an Excel (.xlsx) file with multiple content items. Standalone course rows must appear before any Refresh/New SKU rows referencing them.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <button onClick={() => contentItemsAPI.downloadTemplate()}
              className="w-full flex items-center justify-center gap-2 border border-dashed border-lime/60 text-lime text-sm rounded-lg py-3 hover:bg-lime/5 transition-colors"
              data-testid="content-items-import-download-template">
              <Download className="w-4 h-4" />Download Template
            </button>
            <button onClick={() => importFileRef.current?.click()} disabled={importLoading}
              className="w-full flex items-center justify-center gap-2 border-2 border-dashed border-border text-muted-foreground text-sm rounded-lg py-6 hover:border-lime/40 hover:text-foreground transition-colors disabled:opacity-50"
              data-testid="content-items-import-upload-btn">
              {importLoading
                ? <><span className="animate-spin w-4 h-4 border-2 border-lime border-t-transparent rounded-full inline-block" />Processing...</>
                : <><Upload className="w-5 h-5" />Click to upload XLSX file</>}
            </button>
            {importResult && (
              <div className={`rounded-lg p-3 text-sm ${importResult.created > 0 ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`} data-testid="content-items-import-result">
                <p className="font-medium flex items-center gap-2">
                  {importResult.created > 0 ? <CheckCircle2 className="w-4 h-4 text-green-600" /> : <AlertCircle className="w-4 h-4 text-red-600" />}
                  {importResult.message}
                </p>
                {importResult.skipped?.length > 0 && (
                  <ul className="mt-2 space-y-1 text-xs text-amber-700" data-testid="content-items-import-skipped">
                    {importResult.skipped.map((s, i) => <li key={i}>Row {s.row} ("{s.title}"): {s.reason}</li>)}
                  </ul>
                )}
                {importResult.errors?.length > 0 && (
                  <ul className="mt-2 space-y-1 text-xs text-red-700">
                    {importResult.errors.map((e, i) => <li key={i}>Row {e.row}: {e.error}</li>)}
                  </ul>
                )}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent data-testid="content-item-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete "{deleteTarget?.title}"?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently deletes the content item and its full phase/activity history. This cannot be undone.
              Deletion is blocked if other Refresh/New SKU builds reference it as their parent.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="content-item-delete-cancel-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700"
              data-testid="content-item-delete-confirm-btn"
            >
              {deleting ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={bulkEditOpen} onOpenChange={setBulkEditOpen}>
        <DialogContent data-testid="bulk-edit-dialog">
          <DialogHeader>
            <DialogTitle>Bulk Edit {selectedIds.size} Item{selectedIds.size !== 1 ? 's' : ''}</DialogTitle>
            <DialogDescription>Choose a field and apply a new value to every selected item.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Field to update</Label>
              <Select value={bulkField} onValueChange={(v) => { setBulkField(v); setBulkValue(''); }}>
                <SelectTrigger data-testid="bulk-edit-field-select"><SelectValue placeholder="Choose field..." /></SelectTrigger>
                <SelectContent>
                  {BULK_EDIT_FIELDS.map(f => <SelectItem key={f.key} value={f.key}>{f.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {bulkField && (
              <div>
                <Label>New value</Label>
                <Select value={bulkValue} onValueChange={setBulkValue}>
                  <SelectTrigger data-testid="bulk-edit-value-select"><SelectValue placeholder="Select value..." /></SelectTrigger>
                  <SelectContent>
                    {bulkValueOptions().map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                  </SelectContent>
                </Select>
                {bulkField === 'current_phase' && (
                  <p className="text-xs text-muted-foreground mt-1.5">Marks every phase before this one complete for each selected item — items using different templates are matched by phase number.</p>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkEditOpen(false)}>Cancel</Button>
            <Button
              onClick={handleBulkEditApply}
              disabled={!bulkField || !bulkValue || bulkApplying}
              className="bg-lime text-lime-foreground hover:bg-lime/90"
              data-testid="bulk-edit-apply-btn"
            >
              {bulkApplying ? 'Applying...' : 'Apply'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ContentProjectFormDialog
        open={projectDialogOpen}
        onOpenChange={(v) => { setProjectDialogOpen(v); if (!v) setProjectFromSelection(false); }}
        initial={editProject}
        msOwnerOptions={msOwnerOptions}
        pmOwnerOptions={pmOwnerOptions}
        pos={pos}
        currentUser={user}
        onSaved={loadProjects}
        assignCount={projectFromSelection ? selectedIds.size : 0}
        onCreated={async (project) => {
          if (projectFromSelection && project?.id && selectedIds.size > 0) {
            try {
              await contentItemsAPI.bulkUpdate(Array.from(selectedIds), 'project', project.id);
              toast.success(`Added ${selectedIds.size} item${selectedIds.size !== 1 ? 's' : ''} to "${project.name}"`);
              setSelectedIds(new Set());
              loadItems();
              loadProjects();
            } catch { toast.error('Project created, but failed to assign items'); }
          }
          setProjectFromSelection(false);
        }}
      />
    </div>
  );
};

export default ContentItemsPage;
